import {IssueCreatePage} from '@github-ui/issue-create/IssueCreatePage'
import type {IssueCreateDiscussionData, IssueCreateMetadataTypes} from '@github-ui/issue-create/TemplateArgs'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {RelayCachePreheatContextProvider} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'

import type {AppPayload} from './app-payload'
import type {RepositoryResult} from './internal-issue-new-page-url-arguments-metadata-query-types'
import {PotentialDuplicateSidePanel} from './PotentialDuplicateSidePanel'
import {VALUES} from './values'

// RELAY-CLEANUP(InternalIssueNewPageUrlArgumentsMetadataQuery): RelayCompat widens nullable fields so the
// still-Relay preloaded-query data is assignable. Remove when InternalIssueNewPageUrlArgumentsMetadataQuery leaves Relay.
export type IssueNewContentRepository = RelayCompat<
  Pick<
    RepositoryResult,
    | 'id'
    | 'databaseId'
    | 'name'
    | 'nameWithOwner'
    | 'owner'
    | 'isLocked'
    | 'isPrivate'
    | 'isOwnerEnterpriseManaged'
    | 'visibility'
    | 'isArchived'
    | 'isInOrganization'
    | 'hasIssuesEnabled'
    | 'hasAnyTemplates'
    | 'slashCommandsEnabled'
    | 'viewerCanPush'
    | 'isBlankIssuesEnabled'
    | 'viewerCanCreateIssues'
    | 'viewerInteractionLimitReasonHTML'
    | 'viewerIssueCreationPermissions'
    | 'shortDescriptionHTML'
    | 'issueTemplate'
    | 'issueForm'
    | 'suggestedActors'
    | 'labels'
    | 'discussion'
    | 'milestoneByTitle'
    | 'issueType'
  >
>

/**
 * Renders the new-issue form from the resolved URL-argument metadata.
 *
 * Transport-agnostic: shared by the Relay page (`InternalIssueNewPage`) and the Data Router
 * `issues` app route, which pass the same repository shape from different sources.
 */
export function IssueNewContent({repository}: {repository: IssueNewContentRepository}) {
  const appPayload = useAppPayload<AppPayload>()

  const assigneeNodes = repository.suggestedActors?.nodes
  const assignees =
    assigneeNodes && repository.viewerIssueCreationPermissions?.assignable
      ? assigneeNodes.flatMap(e => (e ? [e] : []))
      : undefined

  const labelNodes = repository.labels?.nodes
  const labels =
    labelNodes && repository.viewerIssueCreationPermissions?.labelable
      ? labelNodes.flatMap(e => (e ? [e] : []))
      : undefined

  const projectNodes = repository.owner?.projectsV2ByNumber?.nodes
  const projects =
    projectNodes && repository.viewerIssueCreationPermissions?.triageable ? projectNodes.filter(e => !!e) : undefined

  const milestone =
    repository.milestoneByTitle && repository.viewerIssueCreationPermissions?.milestoneable
      ? repository.milestoneByTitle
      : undefined

  const issueType =
    repository.issueType &&
    repository.viewerIssueCreationPermissions?.triageable &&
    repository.viewerIssueCreationPermissions?.typeable
      ? repository.issueType
      : undefined

  const discussion = repository.discussion
    ? ({
        title: repository.discussion.title,
        formattedBody: repository.discussion.formattedBody,
        labels: repository.discussion.labels?.edges?.flatMap(e => (e?.node ? [e?.node] : [])),
      } as IssueCreateDiscussionData)
    : undefined

  const initialMetadataValues: IssueCreateMetadataTypes = {
    ...(assignees !== undefined && {assignees}),
    ...(labels !== undefined && {labels}),
    ...(milestone !== undefined && {milestone}),
    ...(projects !== undefined && {projects}),
    ...(issueType !== undefined && {type: issueType}),
    ...(discussion !== undefined && {discussion}),
  }
  const storageKeyPrefix = VALUES.storageKeyPrefix(appPayload)
  const emojiSkinTonePreference = appPayload?.current_user_settings?.preferred_emoji_skin_tone
  const copilotShowFunctionality = appPayload?.current_user_settings?.copilot_show_functionality || false

  return (
    <RelayCachePreheatContextProvider>
      <PotentialDuplicateSidePanel repository={appPayload.scoped_repository}>
        {({onPotentialDuplicateClick, onIssuesToPreheat}) => (
          <IssueCreatePage
            initialMetadataValues={initialMetadataValues}
            storageKeyPrefix={storageKeyPrefix}
            emojiSkinTonePreference={emojiSkinTonePreference}
            copilotShowFunctionality={copilotShowFunctionality}
            currentRepository={repository}
            onPotentialDuplicateClick={onPotentialDuplicateClick}
            onPotentialDuplicatesToPreheat={onIssuesToPreheat}
          />
        )}
      </PotentialDuplicateSidePanel>
    </RelayCachePreheatContextProvider>
  )
}
